import { useState, useId } from 'react';

const tierStyles = {
  exemplary: { ring: 'text-positive', chip: 'border-positive/25 bg-positive-soft text-positive' },
  high: { ring: 'text-accent', chip: 'border-accent/20 bg-accent-soft text-accent-ink' },
  moderate: { ring: 'text-caution', chip: 'border-caution/30 bg-caution-soft text-caution' },
  low: { ring: 'text-ink-faint', chip: 'border-rule-strong bg-paper-sunken text-ink-muted' },
};

/**
 * The transparency badge.
 *
 * Expandable on purpose. A score you can't interrogate is just another opaque
 * ranking signal, and the point of this one is that the whole rubric is
 * published: what each criterion is worth, and exactly which ones this profile
 * has and hasn't met.
 */
export function TransparencyBadge({ transparency, size = 'md', showDetail = true }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  if (!transparency) return null;
  const { score, tier, tierLabel, criteria } = transparency;
  const style = tierStyles[tier] ?? tierStyles.low;
  const canExpand = showDetail && Array.isArray(criteria) && criteria.length > 0;

  const badge = (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${style.chip} ${
        size === 'sm' ? 'text-2xs' : 'text-xs'
      }`}
    >
      <Ring score={score} className={style.ring} size={size === 'sm' ? 12 : 14} />
      <span className="font-semibold tabular-nums">{score}</span>
      <span className="font-medium">{tierLabel} transparency</span>
      {canExpand && (
        <svg
          viewBox="0 0 24 24"
          className={`size-3 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );

  if (!canExpand) return badge;

  return (
    <div className="inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        className="rounded-full focus-visible:outline-2"
      >
        {badge}
      </button>

      {open && (
        <div
          id={panelId}
          className="mt-2 w-80 rounded-lg border border-rule bg-paper-raised p-3 text-left shadow-raised"
        >
          <p className="text-xs text-ink-muted">
            Points for publishing information anyone can check. Nothing here is for sale, and none
            of it measures performance.
          </p>
          <ul className="mt-2.5 space-y-1">
            {criteria.map((c) => (
              <li key={c.key} className="flex items-start gap-2 text-xs">
                <span
                  className={`mt-0.5 flex size-3.5 shrink-0 items-center justify-center rounded-full border ${
                    c.met
                      ? 'border-positive bg-positive text-white'
                      : 'border-rule-strong text-transparent'
                  }`}
                  aria-hidden="true"
                >
                  <svg viewBox="0 0 12 12" className="size-2.5" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M2.5 6.2l2.3 2.3 4.7-4.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className={c.met ? 'text-ink-muted' : 'text-ink'}>
                  {c.label}
                  <span className="ml-1 tabular-nums text-ink-faint">+{c.weight}</span>
                  {!c.met && <span className="block text-ink-faint">{c.hint}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Ring({ score, size = 14, className = '' }) {
  const r = 8;
  const circumference = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 20 20" width={size} height={size} className={className} aria-hidden="true">
      <circle cx="10" cy="10" r={r} fill="none" stroke="currentColor" strokeWidth="3" opacity="0.2" />
      <circle
        cx="10"
        cy="10"
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - score / 100)}
        transform="rotate(-90 10 10)"
      />
    </svg>
  );
}

/**
 * The label that must accompany any self-reported number.
 *
 * Deliberately not subtle and deliberately not optional. A follower count
 * rendered bare reads as verified fact; this is what stops that.
 */
export function SelfReported({ className = '' }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-2xs text-ink-faint ${className}`}
      title="Reported by the creator. Affily has not independently verified this number."
    >
      <svg viewBox="0 0 16 16" className="size-3" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <circle cx="8" cy="8" r="6.5" />
        <path d="M8 7.5v4M8 4.75v.5" strokeLinecap="round" />
      </svg>
      Self-reported
    </span>
  );
}

/** Counterpart label for numbers Affily measured itself. */
export function Tracked({
  className = '',
  children = 'Tracked by Affily',
  title = "Measured by Affily's own tracking, not reported by anyone.",
}) {
  return (
    <span className={`inline-flex items-center gap-1 text-2xs text-positive ${className}`} title={title}>
      <svg viewBox="0 0 16 16" className="size-3" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M3 8.5l3.2 3.2L13 5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {children}
    </span>
  );
}
